import { z } from 'zod/v4'

// Common validation schemas
export const commonSchemas = {
  array: z.array(z.string()).optional(),
  boolean: z.boolean(),
  date: z.string().optional(),
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  name: z.string().min(1, 'Name is required'),
  number: z.number().min(0, 'Must be a positive number'),
  optionalString: z.string().optional(),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  phone: z.string().optional(),
  requiredString: z.string().min(1, 'This field is required'),
  username: z.string().min(3, 'Username must be at least 3 characters')
}

// Form validation schemas
export const formSchemas = {
  admin: z
    .object({
      birth_date: commonSchemas.date,
      blocked: z.number().min(0).max(1),
      confirm_password: z.string().optional(),
      email: commonSchemas.email,
      name: commonSchemas.name,
      password: commonSchemas.password.optional(),
      phone: commonSchemas.phone,
      role_id: z.number().min(1, 'Role is required'),
      status: z.number().min(0).max(1),
      username: commonSchemas.username
    })
    .refine(
      (data) => {
        if (data.password && data.password !== data.confirm_password) {
          return false
        }
        return true
      },
      {
        message: "Passwords don't match",
        path: ['confirm_password']
      }
    ),

  forgotPassword: z.object({
    email: commonSchemas.email
  }),

  login: z.object({
    email: commonSchemas.email,
    password: commonSchemas.password
  }),

  profile: z
    .object({
      birth_date: commonSchemas.date,
      email: commonSchemas.email,
      name: commonSchemas.name,
      password: commonSchemas.password.optional(),
      password_confirmation: z.string().optional(),
      phone: commonSchemas.phone,
      username: commonSchemas.username
    })
    .refine(
      (data) => {
        if (data.password && data.password !== data.password_confirmation) {
          return false
        }
        return true
      },
      {
        message: "Passwords don't match",
        path: ['password_confirmation']
      }
    ),

  resetPassword: z
    .object({
      otp: z
        .string()
        .min(6, 'OTP must be 6 digits')
        .max(6, 'OTP must be 6 digits'),
      password: commonSchemas.password,
      password_confirmation: z.string()
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: "Passwords don't match",
      path: ['password_confirmation']
    }),

  role: z.object({
    ar: z.object({
      description: commonSchemas.optionalString,
      name: commonSchemas.name
    }),
    en: z.object({
      description: commonSchemas.optionalString,
      name: commonSchemas.name
    }),
    permission_type: z.enum(['all', 'custom']),
    permissions: z.array(z.string()).optional()
  })
}

export type AdminFormData = z.infer<typeof formSchemas.admin>
export type ForgotPasswordFormData = z.infer<typeof formSchemas.forgotPassword>
// Form field types
export type LoginFormData = z.infer<typeof formSchemas.login>
export type ProfileFormData = z.infer<typeof formSchemas.profile>
export type ResetPasswordFormData = z.infer<typeof formSchemas.resetPassword>
export type RoleFormData = z.infer<typeof formSchemas.role>

// Common form field configurations
export const formFieldConfigs = {
  permissionTypes: [
    { id: 'all', name: 'All Permissions' },
    { id: 'custom', name: 'Custom Permissions' }
  ],
  status: [
    { id: 1, name: 'Active' },
    { id: 0, name: 'Inactive' }
  ]
}

// Form validation helpers
export const validatePhoneNumber = async (phone: string): Promise<boolean> => {
  if (!phone || phone.trim() === '') return true

  try {
    const { isValidPhoneNumber } = await import('react-phone-number-input')
    return isValidPhoneNumber(phone)
  } catch {
    return false
  }
}

export const parsePhoneNumber = (phone: string) => {
  if (!phone || !phone.startsWith('+')) {
    return { phone: phone || undefined, phone_code: undefined }
  }

  const match = phone.match(/^\+(\d{1,4})/)
  if (match) {
    const phone_code = `+${match[1]}`
    const phoneNumber = phone.substring(phone_code.length)
    return { phone: phoneNumber, phone_code }
  }

  return { phone: phone || undefined, phone_code: undefined }
}

export const combinePhoneNumber = (phone: string, phone_code?: string) => {
  if (!phone) return ''

  // If phone already starts with +, it's in E.164 format
  if (phone.startsWith('+')) {
    return phone
  }

  // If we have a phone_code, combine them
  if (phone_code) {
    // Skip if phone_code looks wrong (same as phone or too long)
    if (phone_code === phone || phone_code.length > 5) {
      return phone
    }

    // Check if phone already includes the code (without +)
    const codeWithoutPlus = phone_code.replace('+', '')
    if (phone.startsWith(codeWithoutPlus)) {
      return `+${phone}`
    }

    // Concatenate code + phone
    return `${phone_code}${phone}`
  }

  // Just return phone as-is
  return phone
}

export const formatDateForInput = (
  dateString: null | string
): null | string => {
  if (!dateString) return null
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    return date.toISOString().split('T')[0]
  } catch {
    return null
  }
}

// Form error handling
export const getFormErrors = (error: any): Record<string, string[]> => {
  if (error?.response?.data?.errors) {
    return error.response.data.errors
  }
  return {}
}

export const hasFormErrors = (errors: Record<string, string[]>): boolean => {
  return Object.keys(errors).length > 0
}

// Form submission helpers
export const createFormSubmissionHandler = <T>(
  mutationFn: (data: T) => Promise<any>,
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void
) => {
  return async (data: T) => {
    try {
      const result = await mutationFn(data)
      onSuccess?.(result)
      return result
    } catch (error) {
      onError?.(error)
      throw error
    }
  }
}

export const zodPhoneNumberSchema = (
  t: Translations<'components.form.validation'>
) =>
  z
    .string()
    .min(13, { message: t('phoneMinLength') })
    .regex(/^\+?\d+$/, t('invalidPhoneNumber'))

/** Phone field that may be left empty; validates format only when a value is entered. */
export const zodOptionalPhoneNumberSchema = (
  t: Translations<'components.form.validation'>
) =>
  z
    .string()
    .optional()
    .superRefine((val, ctx) => {
      if (!val || val.trim() === '') return

      const result = zodPhoneNumberSchema(t).safeParse(val)
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message
          })
        }
      }
    })

export const scrollToFirstInvalidField = (delay = 0) => {
  if (delay > 0) {
    setTimeout(() => {
      const divs = document.querySelectorAll('[data-invalid]')
      if (divs.length > 0) {
        divs[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, delay)
  } else {
    const divs = document.querySelectorAll('[data-invalid]')
    if (divs.length > 0) {
      divs[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }
}

/**
 * Utility type for form default values.
 * Makes all fields optional and allows undefined values.
 * Useful for type assertions when creating defaultValues objects.
 *
 * @example
 * ```typescript
 * const defaultValues = {
 *   name: '',
 *   email: ''
 * } as DefaultValues<FormSchema>
 * ```
 */
export type DefaultValues<T> = {
  [K in keyof T]?: T[K] | undefined
}
